import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { useDevOptionsRevealed, type IconName, type ModalDialogProps } from "@skilitics/designer-ui-components";
import type { FormatStrings } from "@skilitics/localization";
import type { NodeRole } from "@skilitics/nautilus-core";
import { getNodeSlotIdSelector } from "@skilitics/nautilus-core-designer-data";

import { useFeatureSwitch } from "../application/index.ts";
import { commitPendingChanges } from "../events/index.ts";
import { useSaveTemplate } from "../nodes/templates/save-template/index.ts";
import { useSlotCopyClipboard } from "../slots/useSlotCopyClipboard.ts";
import type { DesignerNode } from "../types/index.ts";
import { getManifestSelector } from "./contextualSelectors/appConfigSelectors.ts";
import { getDesignerNodeByIdSelector } from "./contextualSelectors/designerNodeSelectors.ts";
import { getNodeRoleSelector } from "./contextualSelectors/nodeRoleSelectors.ts";
import { getNodeRoleLabel } from "./getNodeRoleLabel.ts";
import { useMutateNodeDesigns } from "./selectorWrappers/index.ts";
import { useCreateNode } from "./selectorWrappers/useCreateNode.ts";

// Roles whose nodes can be pasted into a slot and browsed/saved as templates. Copy and
// Save-as-Template are only offered for these; other roles have no paste target or template
// browser, so those actions would go nowhere.
const rolesWithPasteAndTemplateTargets = new Set<NodeRole>([
  "composable",
  "activity",
  "activity-group",
  "unit",
  "unit-group",
  "activity-frame",
  "interlude",
]);

export type NodeActionBase<TType extends string, TPayload = undefined> = {
  type: TType;
  confirmOptions?: ModalDialogProps;
  disabled?: boolean;
  disabledMessage?: FormatStrings;
  keyboardShortcut?: boolean;
  icon?: IconName;
  label: FormatStrings;
  onSelected: undefined extends TPayload ? (arg?: undefined) => void : (arg: TPayload) => void;
} & (undefined extends TPayload ? { payload?: never } : { payload: TPayload });

export type DeleteNodeAction = NodeActionBase<"delete">;

export type CopyNodeAction = NodeActionBase<"copy">;

export type CutNodeAction = NodeActionBase<"cut">;

export type SaveTemplateNodeAction = NodeActionBase<"saveTemplate">;

export type SaveContentTemplateNodeAction = NodeActionBase<"saveContentTemplate">;

export type ConfigureResultsAction = NodeActionBase<"configureResults", { enabled: boolean }>;

export type ContextNodeAction =
  | DeleteNodeAction
  | CopyNodeAction
  | CutNodeAction
  | SaveTemplateNodeAction
  | SaveContentTemplateNodeAction
  | ConfigureResultsAction;

export interface CreateOptions {
  // Used for workaround to change route after deleting selected node.
  // Remove if/when that is only consumer.
  onAction?: (type: ContextNodeAction["type"], nodeId: number) => void;
}

export const useGetContextNodeActions = () => {
  const getSlotIdById = useSelector(getNodeSlotIdSelector);
  const getById = useSelector(getDesignerNodeByIdSelector);
  const getManifest = useSelector(getManifestSelector);
  const getNodeRole = useSelector(getNodeRoleSelector);
  const { remove, add } = useMutateNodeDesigns();
  const createNode = useCreateNode();
  const { copy } = useSlotCopyClipboard();

  const saveTemplate = useSaveTemplate();
  const isSuperUser = useFeatureSwitch("super-user");
  const devOptionsRevealed = useDevOptionsRevealed();

  // TEMP: this was just something unobtrusive. needs a UI design thought through
  const addAnyConfigureResultsAction = useCallback(
    (node: DesignerNode, actions: ContextNodeAction[]) => {
      const resultsParent: DesignerNode =
        node.typeId === "scene" &&
        "content" in node.childNodes &&
        node.childNodes.content &&
        !Array.isArray(node.childNodes.content)
          ? node.childNodes.content
          : node;

      const { terminus } = getManifest(resultsParent.typeId);
      if (
        terminus.children &&
        "results" in terminus.children &&
        "role" in terminus.children.results &&
        terminus.children.results.role === "query-results"
      ) {
        const onConfigureResultsSelected = ({ enabled }: ConfigureResultsAction["payload"]) => {
          if (enabled) {
            const resultsNode = createNode("assessmentSummary");
            add(resultsNode, { parentId: resultsParent.id, name: "results" });
          } else {
            if (
              "results" in resultsParent.childNodes &&
              resultsParent.childNodes.results &&
              !Array.isArray(resultsParent.childNodes.results)
            ) {
              remove(resultsParent.childNodes.results.id);
            }
          }
        };
        if ("results" in resultsParent.childNodes && resultsParent.childNodes.results) {
          actions.push({
            icon: "pen-field",
            label: { en: "Disable results" },
            type: "configureResults",
            payload: { enabled: false },
            onSelected: onConfigureResultsSelected,
          });
        } else if (isSuperUser) {
          // Note: the UX for this was never completed, so putting behind feature switch for now.
          actions.push({
            icon: "pen-field",
            label: { en: "Enable results" },
            type: "configureResults",
            payload: { enabled: true },
            onSelected: onConfigureResultsSelected,
          });
        }
      }
    },
    [add, createNode, getManifest, isSuperUser, remove],
  );

  return useMemo(
    () =>
      (nodeOrId: DesignerNode | number, { onAction }: CreateOptions = {}): ContextNodeAction[] => {
        const actions: ContextNodeAction[] = [];
        const node = getById(typeof nodeOrId === "number" ? nodeOrId : nodeOrId.id);
        const slotId = getSlotIdById(node.id);
        const nodeRole = getNodeRole(node);
        const nodeRoleLabel = getNodeRoleLabel(nodeRole);

        if (slotId) {
          const parentNode = getById(slotId.parentId);
          const {
            designer: { children: designerChildren },
          } = getManifest(parentNode.typeId);

          const slotResidentNodeId = node.id;
          const removableSpec =
            designerChildren && slotId.name in designerChildren ? designerChildren[slotId.name]?.removable : undefined;
          let canDelete: boolean;
          if (removableSpec) {
            const deleteAction: DeleteNodeAction = {
              type: "delete",
              label: {
                en: `Delete ${nodeRoleLabel ?? "Node"}`,
              },
              icon: "trash-alt",
              onSelected: () => {
                commitPendingChanges();
                remove(node.id);
                onAction?.("delete", node.id);
              },
            };

            if (removableSpec === true) {
              canDelete = true;
              actions.push(deleteAction);
            } else if (typeof removableSpec === "object") {
              const { confirmOptions, keyboardShortcut, isContentsRequired } = removableSpec;
              let disabled = false;
              let disabledMessage = undefined;

              if (isContentsRequired && slotId.name in parentNode.childNodes) {
                const childOrChilds = parentNode.childNodes[slotId.name];
                if (!Array.isArray(childOrChilds) || childOrChilds.length === 1) {
                  disabled = true;
                  disabledMessage = { en: "Cannot delete last item" };
                }
              }
              canDelete = true;
              actions.push({
                ...deleteAction,
                disabled,
                disabledMessage,
                confirmOptions: confirmOptions?.({ node }),
                keyboardShortcut,
              });
            } else if (removableSpec) {
              const spec = removableSpec(node);
              if (typeof spec === "boolean") {
                if (spec) {
                  canDelete = true;
                  actions.push(deleteAction);
                }
              } else {
                const { confirmOptions, keyboardShortcut, disabled, disabledMessage } = spec;
                canDelete = !disabled;
                actions.push({
                  ...deleteAction,
                  confirmOptions: confirmOptions?.({ node }),
                  disabled,
                  disabledMessage,
                  keyboardShortcut,
                });
              }
            }
          }

          // Copy and Save-as-Template only make sense for roles that have a slot to paste into
          // and a template browser to reuse them. For anything else (variables, option items, …)
          // both actions would be dead-ends, so we don't offer them.
          const isPortable = nodeRole !== undefined && rolesWithPasteAndTemplateTargets.has(nodeRole);

          // Super users may copy any node, but only via the revealed dev options so it stays hidden from QA.
          const canCopy = isPortable || (isSuperUser && devOptionsRevealed);

          if (canCopy) {
            actions.push({
              icon: "copy",
              label: { en: nodeRoleLabel ? `Copy ${nodeRoleLabel}` : "Copy" },
              type: "copy",
              onSelected: () => copy({ slotResidentNodeId }),
            });
          }

          // Note unable to cut/paste in most instances due to linkages of dereferencedNodes in useCanImportHere.
          // if (canDelete && canCopy) {
          //   actions.push({
          //     icon: "cut",
          //     label: { en: "Cut" },
          //     type: "cut",
          //     onSelected: () => cut({ slotResidentNodeId }),
          //   });
          // }

          if (isPortable) {
            const content =
              "content" in node.childNodes && node.childNodes.content && !Array.isArray(node.childNodes.content)
                ? node.childNodes.content
                : undefined;
            const composableContent = content?.typeId === "composableActivity" ? content : undefined;

            actions.push({
              icon: "save",
              label: { en: nodeRoleLabel ? `Save ${nodeRoleLabel} as Template` : "Save as Template" },
              type: "saveTemplate",
              // Structural nodes (interlude, learning unit) aren't rendered themselves, so they can't be
              // snapshotted; point the default thumbnail at the composable activity content they wrap.
              onSelected: () => saveTemplate({ nodeId: node.id, thumbnailSourceNodeId: composableContent?.id }),
            });

            // For an activity learning unit, also allow saving just its composable activity content
            // as a template (a learning unit whose content is a composableActivity).
            if (node.typeId === "learningUnit" && composableContent) {
              actions.push({
                icon: "save",
                label: { en: "Save Content as Template" },
                type: "saveContentTemplate",
                onSelected: () => saveTemplate({ nodeId: composableContent.id }),
              });
            }
          }
        }

        addAnyConfigureResultsAction(node, actions);

        return actions;
      },
    [
      addAnyConfigureResultsAction,
      copy,
      devOptionsRevealed,
      getById,
      getManifest,
      getNodeRole,
      getSlotIdById,
      isSuperUser,
      remove,
      saveTemplate,
    ],
  );
};
